import { defineStore } from "pinia";

import orderInvoiceProductsApi from "@/services/api/orderInvoiceProductsApi";

import { getCompanyId } from "@/utils/userUtils";

export const useOrderInvoiceProductStore = defineStore(
  "orderInvoiceProductStore",
  {
    state: () => ({
      products: [],
      companyId: getCompanyId(),
      isLoading: false,
      error: null,

      pagination: {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
      },

      vatKindOptions: [
        { value: "with_vat", label: "С НДС" },
        { value: "zero", label: "НДС 0%" },
        { value: "no_vat", label: "Без НДС" },
      ],

      vatCalculationTypeOptions: [
        { value: "included", label: "НДС включён" },
        { value: "on_top", label: "НДС сверху" },
      ],
    }),

    getters: {
      vatKindLabels: (state) =>
        Object.fromEntries(
          state.vatKindOptions.map((opt) => [opt.value, opt.label])
        ),

      vatCalculationTypeLabels: (state) =>
        Object.fromEntries(
          state.vatCalculationTypeOptions.map((opt) => [
            opt.value,
            opt.label,
          ])
        ),

      activeProducts: (state) =>
        state.products.filter((p) => p.enabled !== false),
    },

    actions: {
      formatVatKind(vatKind) {
        return this.vatKindLabels[vatKind] || "—";
      },

      formatVatCalculationType(type) {
        return this.vatCalculationTypeLabels[type] || "—";
      },

      async getProductsRequest(params = {}) {
        this.isLoading = true;
        try {
          const response = await orderInvoiceProductsApi.index(
            this.companyId,
            params
          );
          this.products = response.data.result || response.data || [];

          const meta = response.data.pagination_meta;
          if (meta) {
            this.pagination = {
              page: meta.page,
              pages: meta.pages,
              series: meta.series,
              next: meta.next,
              prev: meta.prev,
              count: meta.count,
              limit: meta.limit,
            };
          }
        } catch (error) {
          this.error = error;
          console.error("Ошибка при получении продуктов:", error);
        } finally {
          this.isLoading = false;
        }
      },

      async showProduct(id) {
        try {
          const response = await orderInvoiceProductsApi.show(
            this.companyId,
            id
          );
          return response.data.result || response.data || {};
        } catch (error) {
          this.error = error;
          console.error("Ошибка при получении продукта:", error);
        }
      },

      async createProduct(payload) {
        this.isLoading = true;
        try {
          const response = await orderInvoiceProductsApi.create(
            this.companyId,
            payload
          );
          await this.getProductsRequest();
          return response.data.result || response.data || {};
        } catch (error) {
          console.error("Ошибка при создании продукта:", error);
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async updateProduct(id, payload) {
        this.isLoading = true;
        try {
          await orderInvoiceProductsApi.update(
            this.companyId,
            id,
            payload
          );
          await this.getProductsRequest();
        } catch (error) {
          console.error("Ошибка при обновлении продукта:", error);
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async productEnable(id) {
        this.isLoading = true;
        try {
          await orderInvoiceProductsApi.enable(this.companyId, id);
          await this.getProductsRequest();
        } catch (error) {
          console.error("Ошибка при включении продукта:", error);
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async productDisable(id) {
        this.isLoading = true;
        try {
          await orderInvoiceProductsApi.disable(this.companyId, id);
          await this.getProductsRequest();
        } catch (error) {
          console.error("Ошибка при отключении продукта:", error);
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async deleteProduct(id) {
        this.isLoading = true;
        try {
          await orderInvoiceProductsApi.delete(this.companyId, id);
          await this.getProductsRequest();
        } catch (error) {
          console.error("Ошибка при удалении продукта:", error);
          throw error;
        } finally {
          this.isLoading = false;
        }
      },
    },
  }
);
